<script lang="ts">
  import { PERMISSIONS, TOKEN_PAGE, createdText, viewOf, type TunnelAction, type TunnelState } from './lib/cloudflare';

  /** The server's last `tunnel_state`; null while it is asked for. */
  export let state: TunnelState | null;
  /** The action the server is working on, or empty. */
  export let busy: '' | TunnelAction = '';
  /** Why the last action failed, in the server's words. */
  export let error = '';
  /** The token leaves here and is not kept: the field is emptied on the same tick. */
  export let onaction: (action: TunnelAction, token: string) => void;
  export let onclose: () => void;
  /** Opens straight at the token step (or the disconnect review) instead of the summary. */
  export let start: TunnelAction | null = null;

  // The token lives in this variable only: never in storage, an address, a log or a form post.
  let token = '';
  let step: 'main' | 'token' | 'review' = start ? (start === 'disconnect' ? 'review' : 'token') : 'main';
  let action: TunnelAction = start ?? 'setup';

  $: view = viewOf(state);

  function begin(a: TunnelAction) {
    action = a;
    token = '';
    step = a === 'disconnect' ? 'review' : 'token';
  }
  function cancel() {
    token = '';
    step = 'main';
  }
  function confirm() {
    const sent = token;
    token = '';
    step = 'main';
    onaction(action, sent);
  }
  function close() {
    token = '';
    onclose();
  }
</script>

<div class="backdrop" role="dialog" aria-modal="true" aria-label="Cloudflare tunnel">
  <div class="card">
    <h3>Cloudflare tunnel</h3>
    {#if error}<p class="error" role="alert">{error}</p>{/if}
    {#if busy}
      <p role="status">{busy === 'setup' ? 'Setting up the tunnel…' : busy === 'rotate' ? 'Rotating the tunnel…' : 'Disconnecting…'} Your token is used for this one job and is not stored.</p>
    {:else if state === null}
      <p role="status">Asking the server…</p>
    {:else if step === 'token'}
      <p>Create a token on <a href={TOKEN_PAGE} target="_blank" rel="noopener noreferrer">Cloudflare's token page</a> with these permissions, then paste it here once:</p>
      <ul>{#each PERMISSIONS as p}<li>{p}</li>{/each}</ul>
      <label>API token
        <input type="password" autocomplete="off" autocapitalize="off" spellcheck="false" bind:value={token} aria-label="Cloudflare API token" />
      </label>
      <p>The token goes to your server over this connection, is used for this one job and is not stored. This app never calls Cloudflare.</p>
      <div class="buttons">
        <button class="secondary" on:click={cancel}>Back</button>
        <button disabled={!token.trim()} on:click={() => (step = 'review')}>Review</button>
      </div>
    {:else if step === 'review'}
      {#if action === 'disconnect'}
        <p>This removes the tunnel's DNS record, its configuration and the tunnel at Cloudflare, and the credential on this machine. Paste a token to do all of that; leave it empty to remove only the local credential (then delete the rest at Cloudflare yourself).</p>
        <label>API token (optional)
          <input type="password" autocomplete="off" autocapitalize="off" spellcheck="false" bind:value={token} aria-label="Cloudflare API token" />
        </label>
        <div class="buttons">
          <button class="secondary" on:click={cancel}>Back</button>
          <button on:click={confirm}>Disconnect</button>
        </div>
      {:else}
        <p>{action === 'rotate' ? 'This creates a fresh tunnel, switches to it, points the same hostname at it and deletes the old one.' : 'This creates in your Cloudflare account:'}</p>
        {#if action === 'setup'}
          <ul><li>a tunnel for this server</li><li>a hostname one label under your domain</li><li>a DNS record that points the hostname at the tunnel</li></ul>
        {/if}
        <p>Your token is used for this one job and is not stored.</p>
        <div class="buttons">
          <button class="secondary" on:click={cancel}>Back</button>
          <button on:click={confirm}>{action === 'rotate' ? 'Rotate' : view === 'stopped' ? 'Reconnect' : 'Create tunnel'}</button>
        </div>
      {/if}
    {:else if view === 'working'}
      <p><b>{state.hostname}</b>{state.status === 'starting' ? ' (starting)' : ''}</p>
      {#if state.created_at}<p>Created {createdText(state.created_at)}</p>{/if}
      <p>Connect-only credential on this machine: it can connect this tunnel and manage nothing else.</p>
      {#each state.notes ?? [] as n}<p>{n}</p>{/each}
      <div class="buttons">
        <button class="secondary" on:click={() => begin('rotate')}>Rotate</button>
        <button class="secondary" on:click={() => begin('disconnect')}>Disconnect</button>
      </div>
    {:else if view === 'stopped'}
      <p><b>Cloudflare tunnel stopped.</b> {state.hostname} is not reachable. The token may have been revoked or the tunnel deleted at Cloudflare.</p>
      {#each state.notes ?? [] as n}<p>{n}</p>{/each}
      <div class="buttons">
        <button class="secondary" on:click={() => begin('disconnect')}>Disconnect</button>
        <button on:click={() => begin('setup')}>Reconnect</button>
      </div>
    {:else}
      {#if view === 'quick'}<p>This server is reachable through a temporary tunnel at <b>{state.hostname}</b>. The address changes when it restarts. Connect your Cloudflare account for a stable one.</p>
      {:else}<p>Reach this server from anywhere through your own Cloudflare account. You create a token once; there is nothing to type in a terminal.</p>{/if}
      {#each state.notes ?? [] as n}<p>{n}</p>{/each}
      <div class="buttons">
        <button on:click={() => begin('setup')}>Connect Cloudflare</button>
      </div>
    {/if}
    <div class="buttons">
      <button class="secondary" on:click={close}>Close</button>
    </div>
  </div>
</div>

<style>
  .backdrop {
    position: absolute;
    inset: 0;
    z-index: 1500;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: calc(var(--safe-top) + 12px) 12px 12px;
    background: rgba(0, 0, 0, 0.6);
  }
  .card {
    width: 100%;
    max-width: 520px;
    max-height: 100%;
    overflow-y: auto;
    padding: 16px;
    background: var(--bg-secondary);
    border: 1px solid var(--border);
    border-radius: 10px;
  }
  p, li { margin: 8px 0; font-size: 0.8rem; color: var(--fg-muted); }
  ul { margin: 4px 0; padding-left: 20px; }
  .error { color: var(--danger, var(--fg)); }
  label { display: block; margin-top: 8px; font-size: 0.8rem; color: var(--fg-muted); }
  input {
    width: 100%;
    margin-top: 2px;
    color: var(--fg);
    background: var(--bg);
    border: 1px solid var(--border);
    border-radius: 6px;
    padding: 8px;
    font-size: 16px; /* no iOS zoom on focus */
  }
  .buttons { display: flex; justify-content: flex-end; gap: 8px; margin-top: 10px; }
</style>
